import { Avatar } from 'antd';
import { ColumnsType } from 'antd/es/table';
import Box from 'components/Box';
import Button from 'components/Button';
import Modal from 'components/Modal';
import Table from 'components/Table';
import styled from 'styled-components';
import { formatCurrency } from 'utils/formatCurrency';
import colorTheme from 'constants/color';
import Text from 'components/Text';
import HeaderFilter from './HeaderFilter';
import ModalConfirm, { useModalConfirmRef } from 'components/Modal/ModalConfirm';
import reportApis from 'features/report/services/apis';
import toast from 'utils/toast';
import { getMessageError } from 'services/response';
import { IStaffAdjustmentSaleItem } from 'features/report/services/types/api';
import useGetListSaleAdjustment from 'features/report/services/hooks/useGetListSaleAdjustment';
import { verifyAdminPW } from 'widgets/RequestAdminLocalPW/Callback';
import UpdateSaleAdjustmentModal, { useUpdateSaleAdjustmentModal } from '../UpdateSaleAdjustmentModal';
import { IStaffItemData } from 'services/shop/types/staff';

interface ISaleAdjustmentModalProps {
  visible: boolean;
  setVisible: (visible: boolean) => void;
}

const SaleAdjustmentModal = ({ visible, setVisible }: ISaleAdjustmentModalProps) => {
  const { listSaleAdjustment, saleAdjustmentParams, mutate } = useGetListSaleAdjustment();
  const modalConfirm = useModalConfirmRef();
  const updateSaleAdjustmentModal = useUpdateSaleAdjustmentModal();

  const columns: ColumnsType<IStaffAdjustmentSaleItem> = [
    {
      title: 'Tickets',
      dataIndex: 'ticketNumber',
      key: 'ticketNumber',
      align: 'left',
      render: (text: number) => (
        <TicketNumber>
          <Text variant='CONTENT_3' color='text_1'>
            #{text}
          </Text>
        </TicketNumber>
      ),
    },
    {
      title: 'DATE & TIME',
      dataIndex: 'createdDate',
      key: 'createdDate',
      align: 'left',
      render: (text: string) => (text?.trim() ? text : '--'),
    },
    {
      title: 'Staff Sale Increase',
      dataIndex: 'staffNameIncrease',
      key: 'staffNameIncrease',
      align: 'left',
      render: (text: string, record: IStaffAdjustmentSaleItem) => (
        <Box display='flex' alignItems='center' gap='2'>
          <Avatar src={record?.avatarIncrease} size={40} />
          <Text variant='BODY_1' color='text_3'>
            {text || '--'}
          </Text>
        </Box>
      ),
    },
    {
      title: 'Staff Sale Decrease',
      dataIndex: 'staffNameDecrease',
      key: 'staffNameDecrease',
      align: 'left',
      render: (text: string, record: IStaffAdjustmentSaleItem) => (
        <Box display='flex' alignItems='center' gap='2'>
          <Avatar src={record?.avatarDecrease} size={40} />
          <Text variant='BODY_1' color='text_3'>
            {text || '--'}
          </Text>
        </Box>
      ),
    },
    {
      title: 'Amount',
      dataIndex: 'amount',
      key: 'amount',
      align: 'left',
      render: (text: number) => (text ? formatCurrency(text) : '--'),
    },
    {
      title: 'Note/Reason',
      dataIndex: 'note',
      key: 'note',
      align: 'left',
      render: (text: string) => (text?.trim() ? text : '--'),
    },
    {
      title: 'Actions',
      key: 'actions',
      align: 'center',
      width: 200,
      render: (_, record: IStaffAdjustmentSaleItem) => {
        return (
          <ActionsBox>
            <Button ntype='NEW_TYPE' height={2} onClick={() => onOpenEditSaleAdjustmentModal(record)}>
              Edit
            </Button>
            <Button ntype='LIGHT_BUTTON' height={2} onClick={() => handleDelete(record.id)}>
              Delete
            </Button>
          </ActionsBox>
        );
      },
    },
  ];

  const handleChangePage = (page: number) => {
    mutate({ ...saleAdjustmentParams, page });
  };

  const onDelete = async (id: string) => {
    try {
      const res = await reportApis.deleteSaleAdjustment(id);

      if (res?.data?.data) {
        toast.success('Delete sale adjustment successfully');
        mutate(saleAdjustmentParams);
      }
    } catch (error) {
      toast.error(getMessageError(error));
    }
  };

  const handleDelete = (id: string) => {
    modalConfirm.current?.open('Are you sure you want to delete this adjustment?', () => onDelete(id));
  };

  const onOpenEditSaleAdjustmentModal = (item: IStaffAdjustmentSaleItem) =>
    verifyAdminPW((_pin?: string, data?: IStaffItemData) => {
      updateSaleAdjustmentModal.current?.open(item, data?.id);
    });

  return (
    <Modal
      modalTitle='Sale Adjustment'
      visible={visible}
      onClose={() => setVisible(false)}
      footer={null}
      width={1440}
      destroyOnClose
      noneBodyStyle
      v2
    >
      <Container>
        <HeaderFilter />
        <ScrollableTableSection>
          <TableStyled>
            <Table
              columns={columns}
              dataSource={listSaleAdjustment?.content || []}
              rowKey='id'
              // loading={loading}
              pagination={{
                current: saleAdjustmentParams.page,
                pageSize: saleAdjustmentParams.size,
                total: listSaleAdjustment?.totalElements || 0,
                showSizeChanger: false,
              }}
              handleChangePage={handleChangePage}
            />
          </TableStyled>
        </ScrollableTableSection>
        <FixedBottomSection>
          <Button ntype='SECONDARY' width='100%' onClick={() => setVisible(false)}>
            Close
          </Button>
        </FixedBottomSection>
      </Container>
      <ModalConfirm ref={modalConfirm} />
      <UpdateSaleAdjustmentModal ref={updateSaleAdjustmentModal} />
    </Modal>
  );
};

export default SaleAdjustmentModal;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  height: calc(100vh - 250px);
  max-height: 900px;
  padding: 16px;
  overflow: hidden;
`;

const ScrollableTableSection = styled.div`
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  display: flex;
  flex-direction: column;
  min-height: 0;
  margin-top: 16px;

  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb {
    background: #888;
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: #555;
  }
`;

const FixedBottomSection = styled.div`
  flex-shrink: 0;
  background: #fff;
  z-index: 10;
  padding-top: 16px;
`;

const TableStyled = styled.div`
  flex-shrink: 0;
  .ant-table-wrapper {
    overflow: visible;
    .ant-table {
      .ant-table-container {
        .ant-table-content {
          overflow: visible;
          border: none;
          .ant-table-thead {
            overflow: hidden;
            position: sticky;
            top: 0;
            z-index: 10;

            th.ant-table-cell {
              position: sticky;
              top: 0;
              z-index: 99;
              background: #6fabb6 !important;
              border-bottom: 1px solid #ccd4dc;
              color: #fff !important;
              height: 52px;
              padding: 8px;
              font-family: Poppins;
              font-size: 14px;
              font-style: normal;
              font-weight: 600;
              line-height: normal;
              position: relative;
              &::before {
                content: none;
              }

              &:not(:last-child)::after {
                content: '';
                position: absolute;
                right: 0;
                top: 8px;
                bottom: 8px;
                width: 2px;
                background: rgba(255, 255, 255, 0.3);
                z-index: 1;
              }
            }

            tr th:first-child {
              border-top-left-radius: 8px !important;
            }

            tr th:last-child {
              border-top-right-radius: 8px !important;
            }
          }
          .ant-table-tbody {
            .ant-table-row {
              background: #ffffff;
              &:nth-child(even) {
                background: #e4f4f6;
              }
            }
          }
        }
      }
    }

    .ant-table .ant-table-thead th.ant-table-cell {
      position: sticky;
      top: 0;
      z-index: 99;
      background: #6fabb6 !important;
      border-bottom: 1px solid #ccd4dc;
      color: #fff !important;
      height: 52px;
      padding: 8px;
      font-family: Poppins;
      font-size: 14px;
      font-style: normal;
      font-weight: 600;
      line-height: normal;
      position: relative;
      &::before {
        content: none;
      }

      &:not(:last-child)::after {
        content: '';
        position: absolute;
        right: 0;
        top: 8px;
        bottom: 8px;
        width: 2px;
        background: rgba(255, 255, 255, 0.3);
      }
    }

    .ant-table .ant-table-thead tr th:first-child {
      border-top-left-radius: 8px !important;
    }

    .ant-table .ant-table-thead tr th:last-child {
      border-top-right-radius: 8px !important;
    }

    .ant-table-tbody td.ant-table-cell {
      height: auto;
      padding: 2px 8px;
      color: #131921 !important;
      font-family: Poppins;
      font-size: 14px;
      font-style: normal;
      font-weight: 400;
      line-height: 22px;
    }

    .ant-table-tbody .ant-table-row:nth-child(even) td {
      background: #e4f4f6;
    }

    .ant-table-pagination {
      border: none;

      .ant-pagination-item.ant-pagination-item-active {
        background-color: #2d6772;
        border: none;
      }
      .ant-pagination-item.ant-pagination-item-active a {
        color: #fff;
      }
    }
  }
`;

const ActionsBox = styled.div`
  display: flex;
  gap: 8px;
  justify-content: center;
  align-items: center;

  .color {
    font-size: 14px;
  }
`;

const TicketNumber = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${colorTheme.v2_neutral_6};
  border-radius: 4px;
  width: fit-content;
  padding: 4px 8px;
`;
